import { AnnualReportNavigationDirection, AnnualReportProps } from '../AnnualReport';
import {
    Badge,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    CardFooter,
    Col,
    Form,
    FormGroup,
    Label,
    ListGroupItem,
    Row,
} from 'reactstrap';
import { Collapse, Input } from '../../../components';
import { DocumentUpload, RelatedDocumentDto } from '../../../common/Types';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';

import { ApiUriArms } from '../../../services/HttpHelper';
import Dropzone from 'react-dropzone';
import { professionService } from '../../../services/ProfessionAdministration';
import { programService } from '../../../services/ProgramService';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

const DropZoneContainer = styled.section`
    border: dashed 5px #cccccc;
    > div {
        height: 80px;
    }
`;

const RelatedDocuments = (props: AnnualReportProps) => {
    const [newDocuments, setNewDocuments] = useState<DocumentUpload[]>([]);
    const [uploadedDocuments, setUploadedDocuments] = useState<RelatedDocumentDto[]>();
    const [reloadDocuments, setReloadDocuments] = useState<boolean>(true);
    const [categories, setCategories] = useState<any[]>();
    const [selectedDoc, setSelectedDoc] = useState<RelatedDocumentDto>();
    const [acknowledged, setAcknowledged] = useState(props.hasTabAlreadyBeenSubmitted);
    const {
        control: editControl,
        handleSubmit: handleEditSubmit,
        setValue: setEditValue,
        reset: resetEdit,
        formState: { isSubmitting: isEditSubmitting },
    } = useForm();
    const {
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'docs',
    });

    const addStagedDoc = (dropData) => {
        if (dropData && Array.isArray(dropData)) {
            const docs: DocumentUpload[] = [];
            const maxFileSizeMB = 100; // Match backend limit
            const bytesInMB = 1024 * 1024;

            for (const file of dropData) {
                // Check file size before processing
                if (file.size > maxFileSizeMB * bytesInMB) {
                    toast.error(
                        `File "${file.name}" is too large. Maximum size is ${maxFileSizeMB}MB, but file is ${(
                            file.size / bytesInMB
                        ).toFixed(1)}MB.`,
                    );
                    continue; // Skip this file
                }

                file.fileId = uuidv4();

                const relDocDto = new RelatedDocumentDto({
                    documentId: 0,
                    activeYear: props.reportYear,
                    programId: props.program.programId,
                    fileUrl: file.path,
                    isDeleted: false,
                    isAwardLetter: false,
                });

                append(relDocDto);

                const docUpload = new DocumentUpload({
                    fileUpload: file,
                    dto: relDocDto,
                });
                docs.push(docUpload);
            }

            if (docs.length > 0) {
                setNewDocuments([...newDocuments, ...docs]);
            }
        }
    };

    const uploadDocs = (docs): Promise<void> => {
        return new Promise((res) => {
            if (!docs) res();

            docs.forEach((doc) => {
                const toastId = toast.info(`Saving ${doc.dto?.fileUrl}...`, {
                    autoClose: false,
                    closeOnClick: false,
                    draggable: false,
                });
                programService
                    .uploadProgramDocument(doc)
                    .then((response) => {
                        toast.update(toastId, {
                            type: 'success',
                            render: 'Document saved.',
                            autoClose: 5000, // Auto-close success messages after 5 seconds
                            closeOnClick: true,
                            draggable: true,
                        });

                        // if (!response.isDeleted) {
                        //     setUploadedDocuments([...(excludeExists ?? []), response]);
                        // } else {
                        //     setUploadedDocuments([...(excludeExists ?? [])]);
                        // }

                        setNewDocuments([]);
                        setSelectedDoc(undefined);
                        setAcknowledged(false);
                    })
                    .catch((error) => {
                        // Extract the error message from the backend response
                        let errorMessage = `Failed to upload ${doc.dto?.fileUrl}.`;

                        if (error && typeof error === 'string') {
                            errorMessage = error;
                        } else if (error && error.message) {
                            errorMessage = error.message;
                        } else if (error && error.error) {
                            errorMessage = error.error;
                        }

                        toast.update(toastId, {
                            type: 'error',
                            render: errorMessage,
                            autoClose: false, // Keep error messages visible until user dismisses
                            closeOnClick: true,
                            draggable: true,
                        });
                    })
                    .finally(() => {
                        res();
                    });
            });
        });
    };

    const onEditSubmit = async (formData): Promise<void> => {
        await uploadDocs([{ dto: new RelatedDocumentDto(formData) }]);
        setReloadDocuments(true);
        return;
    };

    const onSubmit = async (formData): Promise<void> => {
        const newDocs = newDocuments.map((doc) => {
            const inputVals = formData.docs.find((x) => x.fileUrl === doc.dto?.fileUrl);
            return {
                ...doc,
                dto: {
                    ...doc.dto,
                    title: inputVals.title,
                    categoryId: inputVals.categoryId ? +inputVals.categoryId : null,
                },
            } as DocumentUpload;
        });
        setNewDocuments(newDocs);
        await uploadDocs(newDocs);
        setReloadDocuments(true);
        return;
    };

    // Get already uploaded documents
    useEffect(() => {
        const getDocs = () => {
            if (reloadDocuments) {
                setReloadDocuments(false);
                programService
                    .getProgramDocuments(props.program.programId, props.reportYear ?? new Date().getFullYear())
                    .then((response) => {
                        setUploadedDocuments(response);
                    })
                    .catch(() => {
                        toast.error('Error loading documents.');
                    });
            }
        };

        getDocs();
    }, [setUploadedDocuments, props.program.programId, props.reportYear, reloadDocuments]);

    // Get document categories
    useEffect(() => {
        const getDocumentCategories = () => {
            professionService
                .getDocumentCategories(props.program.professionId)
                .then((response) => {
                    setCategories(response);
                })
                .catch(() => {
                    toast.error('Error loading document categories.');
                });
        };

        getDocumentCategories();
    }, [setCategories, props.program.professionId]);

    // Remove staged document
    const removeDoc = (doc, index) => {
        setNewDocuments([...(newDocuments.filter((x) => x.dto?.fileUrl !== doc.fileUrl) ?? [])]);
        remove(index);
    };

    // Initialize edit form when a document is selected
    useEffect(() => {
        if (selectedDoc) {
            resetEdit(selectedDoc);
        }
    }, [selectedDoc, resetEdit]);

    // Remove uploaded document
    const removeUploadedDoc = (doc) => {
        setEditValue('isDeleted', true);
        doc.isDeleted = true;
        setUploadedDocuments([...(uploadedDocuments?.filter((x) => x.documentId !== doc.documentId) ?? []), doc]);
    };

    return (
        <Card>
            <CardBody>
                <h3 className="card-title">Related Documents</h3>
                {props.canEdit && (
                    <Dropzone onDrop={(acceptedFiles) => addStagedDoc(acceptedFiles)}>
                        {({ getRootProps, getInputProps }) => (
                            <DropZoneContainer>
                                <div {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <p style={{ textAlign: 'center', lineHeight: '40px', margin: '20px 0' }}>
                                        Drag &apos;n&apos; drop some files here, or click to select files
                                        <br />
                                        <small style={{ color: '#6c757d' }}>Maximum file size: 100MB per file</small>
                                    </p>
                                </div>
                            </DropZoneContainer>
                        )}
                    </Dropzone>
                )}
                <Form noValidate onSubmit={handleSubmit(onSubmit, (validationErrors) => {
                    const fieldNames = Object.keys(validationErrors);
                    toast.error(`Please fix the following fields: ${fieldNames.join(', ')}`);
                })}>
                    {newDocuments.length > 0 && (
                        <div className="mt-3">
                            <h4 className="mb-3">Staged Documents for {props.reportYear}</h4>
                            {fields.map((doc, i) => (
                                <ListGroupItem style={{ display: 'flex' }} key={i}>
                                    <FormGroup style={{ paddingRight: '8px' }}>
                                        <Label>Title</Label>
                                        <Controller
                                            name={`docs[${i}].title` as any}
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    style={{ width: '250px' }}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                    <FormGroup style={{ paddingRight: '8px', flex: 1 }}>
                                        <Label>Category</Label>
                                        <Controller
                                            name={`docs[${i}].categoryId` as any}
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="select"
                                                    style={{ width: '100%' }}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                >
                                                    {categories?.map((cat, cati) => (
                                                        <option
                                                            key={cati}
                                                            value={cat.categoryId}
                                                            style={{ whiteSpace: 'nowrap' }}
                                                        >
                                                            {cat.title}
                                                        </option>
                                                    ))}
                                                </Input>
                                            )}
                                        />
                                    </FormGroup>
                                    <FormGroup style={{ paddingRight: '8px', flex: 1 }}>
                                        <Label>File Name</Label>
                                        <div style={{ lineHeight: '40px' }}>{(doc as any).fileUrl}</div>
                                        <Controller
                                            name={`docs[${i}].fileUrl` as any}
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="hidden"
                                                    {...field}
                                                    value={field.value ?? (doc as any).fileUrl ?? ''}
                                                />
                                            )}
                                        />
                                    </FormGroup>
                                    <div className="text-right">
                                        <ButtonGroup>
                                            <Button color="danger" onClick={() => removeDoc(doc, i)}>
                                                <i className={'remixicon-delete-bin-line'}></i>
                                            </Button>
                                        </ButtonGroup>
                                    </div>
                                </ListGroupItem>
                            ))}
                            {props.canEdit && (
                                <Row className="mt-3">
                                    <Col>
                                        <Button disabled={isSubmitting} color="primary" type="submit">
                                            Upload File(s)
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )}
                </Form>
                <Form noValidate onSubmit={handleEditSubmit(onEditSubmit)}>
                    <div className="mt-3">
                        <h4 className="mb-3">Uploaded Documents for {props.reportYear}</h4>

                        {uploadedDocuments &&
                            uploadedDocuments
                                .sort((docA, docB) => {
                                    const dateA = new Date(docA.dateCreated ?? '');
                                    const dateB = new Date(docB.dateCreated ?? '');
                                    return (dateB as any) - (dateA as any);
                                })
                                .map((doc, i) => {
                                    return (
                                        <ListGroupItem key={doc.documentId ?? i}>
                                            <div style={{ display: 'flex' }}>
                                                <div style={{ width: '50px' }}>
                                                    {doc.fileUrl && (
                                                        <a href={`${ApiUriArms}/document/download/${doc.fileUrl}`}>
                                                            <i
                                                                style={{ fontSize: '32px' }}
                                                                className={'remixicon-file-text-line'}
                                                            />
                                                        </a>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                    <div style={{ flex: 1, display: 'flex' }}>
                                                        <h5>
                                                            {doc.title} <Badge>{doc.category?.title}</Badge>
                                                        </h5>
                                                        <div style={{ flex: 1 }} className="text-right">
                                                            <ButtonGroup>
                                                                {doc.documentId === selectedDoc?.documentId ? (
                                                                    JSON.parse(doc.isDeleted?.toString() ?? 'false') ? (
                                                                        <span className="text-danger">
                                                                            Click Save to delete
                                                                        </span>
                                                                    ) : (
                                                                        <Button
                                                                            color="danger"
                                                                            onClick={() => removeUploadedDoc(doc)}
                                                                        >
                                                                            <i
                                                                                className={'remixicon-delete-bin-line'}
                                                                            ></i>
                                                                        </Button>
                                                                    )
                                                                ) : (
                                                                    <Button
                                                                        color="info"
                                                                        onClick={() => setSelectedDoc(doc)}
                                                                    >
                                                                        <i className={'remixicon-pencil-line'}></i>
                                                                    </Button>
                                                                )}
                                                            </ButtonGroup>
                                                        </div>
                                                    </div>
                                                    <div style={{ flex: 1, display: 'flex' }}>
                                                        <div style={{ flex: 1 }}>
                                                            {doc.fileUrl && (
                                                                <a
                                                                    href={`${ApiUriArms}/document/download/${doc.fileUrl}`}
                                                                >
                                                                    {doc.fileDownloadName}
                                                                </a>
                                                            )}
                                                        </div>
                                                        <div className="text-right">
                                                            Last Edited:{' '}
                                                            {doc.lastEdited
                                                                ? new Date(doc.lastEdited).toDateString()
                                                                : 'NA'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <Collapse isOpen={doc.documentId === selectedDoc?.documentId}>
                                                {doc.documentId === selectedDoc?.documentId && (
                                                    <div style={{ display: 'flex' }}>
                                                        <FormGroup style={{ paddingRight: '8px' }}>
                                                            <Label>Title</Label>
                                                            <Controller
                                                                name="title"
                                                                control={editControl}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        style={{ width: '250px' }}
                                                                        {...field}
                                                                        value={field.value ?? doc.title ?? ''}
                                                                    />
                                                                )}
                                                            />
                                                        </FormGroup>
                                                        <FormGroup style={{ paddingRight: '8px' }}>
                                                            <Label>Category</Label>
                                                            <Controller
                                                                name="categoryId"
                                                                control={editControl}
                                                                render={({ field }) => (
                                                                    <Input
                                                                        type="select"
                                                                        style={{ width: '100%' }}
                                                                        {...field}
                                                                        value={field.value ?? doc.categoryId ?? ''}
                                                                    >
                                                                        {categories?.map((cat, cati) => (
                                                                            <option
                                                                                key={cati}
                                                                                value={cat.categoryId}
                                                                                style={{ whiteSpace: 'nowrap' }}
                                                                            >
                                                                                {cat.title}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                )}
                                                            />
                                                        </FormGroup>
                                                        <Controller
                                                            name="documentId"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={field.value ?? doc?.documentId ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="activeYear"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={field.value ?? doc?.activeYear ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="programId"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={field.value ?? doc?.programId ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="fileUrl"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={field.value ?? doc?.fileUrl ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="createdBy"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={field.value ?? doc?.createdBy ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="dateCreated"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={
                                                                        field.value ??
                                                                        doc?.dateCreated?.toString() ??
                                                                        ''
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="lastEdited"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={
                                                                        field.value ?? doc?.lastEdited?.toString() ?? ''
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="editedBy"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={field.value ?? doc?.editedBy ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="isDeleted"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={
                                                                        field.value ?? doc?.isDeleted?.toString() ?? ''
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="isAwardLetter"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={
                                                                        field.value ??
                                                                        doc?.isAwardLetter?.toString() ??
                                                                        ''
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="createdByString"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={field.value ?? doc?.createdByString ?? ''}
                                                                />
                                                            )}
                                                        />
                                                        <Controller
                                                            name="editedByString"
                                                            control={editControl}
                                                            render={({ field }) => (
                                                                <input
                                                                    type="hidden"
                                                                    {...field}
                                                                    value={field.value ?? doc?.editedByString ?? ''}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                )}
                                                {props.canEdit && (
                                                    <Row>
                                                        <Col>
                                                            <Button
                                                                disabled={isEditSubmitting}
                                                                color="primary"
                                                                type="submit"
                                                            >
                                                                Save
                                                            </Button>
                                                            &nbsp;
                                                            <Button
                                                                onClick={() => {
                                                                    setSelectedDoc(undefined);
                                                                    doc.isDeleted = false;
                                                                    resetEdit();
                                                                }}
                                                                color="secondary"
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </Collapse>
                                        </ListGroupItem>
                                    );
                                })}
                    </div>
                </Form>
                <Card className={'mt-3'}>
                    <CardBody>
                        <h5 className={'card-title'}>{`Acknowledgment`}</h5>
                        <FormGroup>
                            <div className={'custom-control custom-checkbox'}>
                                <Input
                                    type="checkbox"
                                    checked={acknowledged}
                                    id={'acknowledgment'}
                                    onChange={() => setAcknowledged(!acknowledged)}
                                    className={'custom-control-input'}
                                />
                                {(uploadedDocuments?.length || 0) > 0 && (
                                    <Label
                                        for={`acknowledgment`}
                                        className={'custom-control-label'}
                                    >{`I have uploaded all required and appropriate documents.`}</Label>
                                )}
                                {(uploadedDocuments?.length || 0) === 0 && (
                                    <Label
                                        for={`acknowledgment`}
                                        className={'custom-control-label'}
                                    >{`I have no documents to upload.`}</Label>
                                )}
                            </div>
                        </FormGroup>
                    </CardBody>
                </Card>
            </CardBody>
            {props.canEdit && (
                <CardFooter className={'d-flex justify-content-between'}>
                    <Button
                        type={'button'}
                        className={'btn btn-warning'}
                        onClick={() => props.onNavigate(AnnualReportNavigationDirection.Back)}
                    >
                        <i className={'mdi mdi-arrow-left'} />
                        <span className={'mr-1'}>{`Previous`}</span>
                    </Button>

                    {acknowledged && (
                        <Button
                            style={{ zIndex: 1 }}
                            className={'btn btn-info'}
                            disabled={!acknowledged}
                            onClick={() => props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward)}
                        >
                            <span className={'mr-1'}>{`Continue`}</span>
                            <i className={'mdi mdi-arrow-right'} />
                        </Button>
                    )}
                    {!acknowledged && (
                        <Label className={'text-danger'}>
                            {`Please check the acknowledgement checkbox above to continue.`}
                        </Label>
                    )}
                </CardFooter>
            )}
        </Card>
    );
};

export default RelatedDocuments;
